import type { IAccount } from "@/modules/account/common/types";
import { groupCommissionFieldKeys } from "@/modules/transaction/common/constants";
import { TransactionTypes, TransferKeys } from "@/modules/transaction/common/enum";
import type {
  IGroupTransfer,
  IGroupTransferBeneficiary,
  IGroupTransferForm,
  IGroupTransferRequest,
} from "@/modules/transaction/common/types";
import { getAccountOptions } from "@/modules/transaction/helpers";
import {
  useCreateGroupTransferMutation,
  useLazyGetGroupTransferCommissionQuery,
  useUpdateGroupTransferMutation
} from "@/modules/transaction/services";
import { setGroupTransferCurrency } from "@/modules/transaction/state";
import { DATE_FORMAT, INPUT_MAX_LIMIT } from "@/shared/common/constants";
import { Storage } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import { Amount, OwnAccountSelectOption, Select, TransferLayout } from "@/shared/components";
import {
  clampToCurrentDate,
  cleanupAmount,
  formatDate,
  getFromLocalStorage,
  trimAll,
} from "@/shared/helpers";
import { useAppDispatch, useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { SimpleDatePicker } from "@ab.uitools/ui-library/components/DatePicker";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import uuid4 from "uuid4";

interface Props {
  accountsList: IAccount[];
  groupTransferBeneficiariesList: IGroupTransferBeneficiary[];
  isEdit?: boolean;
  isCopy?: boolean;
  groupTransfer?: IGroupTransfer;
  isGetTransferLoading?: boolean;
}

export const GroupTransferForm = ({
  isEdit,
  isCopy,
  accountsList,
  groupTransfer,
  groupTransferBeneficiariesList,
  isGetTransferLoading,
}: Props) => {
  const { t, i18n: { language } } = useTranslation();
  const { setValue, watch, errors = {}, reset } = useFormProps();
  const dispatch = useAppDispatch();
  const idempotencyKey = useRef(uuid4()).current;

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const [getCommission] = useLazyGetGroupTransferCommissionQuery();
  const [createGroupTransferApi, { isLoading: isCreateTransferLoading }] = useCreateGroupTransferMutation();
  const [updateGroupTransferApi, { isLoading: isUpdateTransferLoading }] = useUpdateGroupTransferMutation();

  const accountOptions = useMemo(() => {
    return getAccountOptions({ accountsList });
  }, [accountsList]);

  const values = watch?.() || {};

  const totalAmount = useMemo(() => {
    return groupTransferBeneficiariesList.reduce(
      (acc, item) => acc + +cleanupAmount(`${item.amount}`),
      0
    );
  }, [groupTransferBeneficiariesList]);

  const handleClickTarget = (target: SingleValue<TOption>) => {
    const { currency } = target?.meta as IAccount;
    if (!!values.currency && values.currency !== currency) {
      setValue(TransferKeys.Amount, "");
    }
    setValue(TransferKeys.Currency, currency);
    dispatch(setGroupTransferCurrency(currency));
  };

  const selectedDebitAccount = useMemo(() => {
    return accountOptions?.find(({ value }) => value === values.debitAccount);
  }, [values]);

  const normalizedBeneficiariesList = useMemo(() => {
    return groupTransferBeneficiariesList.map((item) => ({
      ...item, amount: +cleanupAmount(`${item.amount}`)
    }));
  }, [groupTransferBeneficiariesList]);

  const groupTransferAction = useCallback((values: IGroupTransferForm) => {
    const requestData: IGroupTransferRequest = {
      purpose: values.purpose,
      tin: getFromLocalStorage(Storage.Tin) || "",
      beneficiaries: normalizedBeneficiariesList,
      transactionNumber: values.transactionNumber,
      scheduleDate: formatDate(values.scheduleDate, DATE_FORMAT.YYYY_MM_DD),
      amount: totalAmount,
      socialCardNumber: "",
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount),
      commissionAccount: values.commissionAccount ? accountsList?.find(({ number }) => {
        return number === values.commissionAccount;
      }) : undefined,
    };
    return isEdit ?
      updateGroupTransferApi({
        data: requestData,
        id: `${groupTransfer?.id}`,
      }).unwrap() :
      createGroupTransferApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [groupTransfer, totalAmount, groupTransferBeneficiariesList, accountsList]);

  const changes = watch?.(groupCommissionFieldKeys).toString();

  const setDefaultValues = () => {
    const data = {
      debitAccount: groupTransfer?.debitAccount.number,
      transactionNumber: isEdit ? groupTransfer?.transactionNumber : values.transactionNumber,
      beneficiaries: groupTransfer?.beneficiaries,
      scheduleDate: clampToCurrentDate(groupTransfer?.scheduleDate),
      purpose: groupTransfer?.purpose,
      currency: groupTransfer?.currency,
      amount: groupTransfer?.amount,
      commissionAccount: groupTransfer?.commissionAccount?.number,
    };
    reset?.(data);
    setDefaultData({
      ...data,
      scheduleDate: new Date(groupTransfer?.scheduleDate as string),
    });
  };

  const currentData = useMemo(() => ({
    debitAccount: values.debitAccount,
    transactionNumber: values.transactionNumber,
    beneficiaries: groupTransferBeneficiariesList,
    scheduleDate: new Date(values.scheduleDate),
    purpose: values.purpose,
    currency: values.currency,
    amount: totalAmount,
    commissionAccount: values.commissionAccount,
  }), [values, totalAmount]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  const fieldChanges = useMemo(() => {
    return {
      debitAccount: trimAll(`${values.debitAccount || ""}`),
      creditAccounts: trimAll(`${groupTransferBeneficiariesList[0]?.receiverAccount}`),
      amount: trimAll(`${totalAmount || ""}`),
    };
  }, [changes, totalAmount, groupTransferBeneficiariesList]);

  const canGetCommission = useMemo(() => {
    return !!(
      fieldChanges.creditAccounts &&
      fieldChanges.debitAccount &&
      fieldChanges.amount
    );
  }, [fieldChanges]);

  useEffect(() => {
    setValue(TransferKeys.Amount, totalAmount);
  }, [totalAmount]);

  useEffect(() => {
    if ((isEdit || isCopy) && !!groupTransfer?.id) {
      setDefaultValues();
    }
  }, [isEdit, isCopy, groupTransfer]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.ScheduleDate, new Date());
    }
  }, []);

  return (
    <TransferLayout
      page="Group transfer"
      accountsList={accountsList}
      isLoading={isCreateTransferLoading || isUpdateTransferLoading}
      isGetTransferLoading={isGetTransferLoading}
      transactionType={TransactionTypes.GroupTransfer}
      isEdit={isEdit}
      hasChanges={hasChanges}
      getCommission={getCommission}
      fieldChanges={fieldChanges}
      isSaveDisabled={!groupTransferBeneficiariesList?.length}
      isSignDisabled={!groupTransferBeneficiariesList?.length}
      canGetCommission={canGetCommission}
      getTransferId={groupTransferAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="debitAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    className="custom-select"
                    options={accountOptions}
                    label={t("transfer.debitAccount")}
                    placeholder={t("transfer.chooseDebitAccount")}
                    handleChange={(opt) => handleClickTarget(opt as SingleValue<TOption>)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <div className="mb-32">
                <Text className="mb-8" weight="semibold">
                  {t("transfer.group.totalAmount")}
                </Text>
                {totalAmount ? (
                  <Amount
                    weight="regular"
                    size="standard"
                    position="left"
                    currency={selectedDebitAccount?.meta?.currency}
                    amount={totalAmount}/>
                ) : "-"}
              </div>
              <FormField
                name="purpose"
                className="mb-8"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="transactionNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("transfer.transactionNumber")}
                    placeholder={t("transfer.enterTransactionNumber")}
                  />
                )}/>
              <FormField
                name="scheduleDate"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <SimpleDatePicker
                    {...props}
                    required
                    closeOnScroll
                    locale={language}
                    format={DATE_FORMAT.DD_MM_YYYY}
                    label={t("general.date")}
                    value={props.value as Date}
                    minDate={new Date()}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </TransferLayout>
  );
};
